<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue';
import { useElementSize } from '@vueuse/core';

import AppRawButton from '../appRawButton/AppRawButton.vue';
import AppSvgIcon from '../appSvgIcon/AppSvgIcon.vue';
import AppFoldableTransition from '../transitions/appFoldableTransition/AppFoldableTransition.vue';

interface AppExpandableButtonProps {
  expanded?: boolean;
  disabled?: boolean;
}

interface AppExpandableButtonEmits {
  (event: 'expanded', value: boolean): void;
  (event: 'click'): void;
}

const props = withDefaults(defineProps<AppExpandableButtonProps>(), {
  expanded: undefined,
});

const emit = defineEmits<AppExpandableButtonEmits>();

const internalExpanded = ref<boolean>(false);

const isExpanded = computed<boolean>(() =>
  props.expanded !== undefined ? props.expanded : internalExpanded.value
);

const chevronIcon = computed<'chevronDown' | 'chevronUp'>(() =>
  isExpanded.value ? 'chevronUp' : 'chevronDown'
);
const contentRef = useTemplateRef('contentRef');
const { height } = useElementSize(contentRef);

function toggleExpanded(): void {
  if (props.expanded !== undefined) {
    emit('expanded', !isExpanded.value);
  } else {
    internalExpanded.value = !internalExpanded.value;
  }
  emit('click');
}
</script>

<template>
  <div class="app-expandable-button">
    <AppRawButton
      :disabled="disabled"
      class="expandable-button"
      :aria-expanded="isExpanded"
      @click="toggleExpanded"
    >
      <div class="expandable-button-wrapper">
        <div class="left-icon" :class="{ disabled }">
          <slot name="left-icon" />
        </div>
        <div class="content">
          <div class="summary-content">
            <div class="title" :class="{ disabled }">
              <slot name="title" />
            </div>
            <div class="subtitle" :class="{ disabled }">
              <slot name="subtitle" />
            </div>
          </div>
        </div>
        <AppSvgIcon
          :name="chevronIcon"
          size="16"
          class="right-icon"
          :class="{ disabled }"
        />
      </div>
    </AppRawButton>

    <AppFoldableTransition :height="height">
      <div v-if="isExpanded">
        <div ref="contentRef">
          <slot name="content" />
        </div>
      </div>
    </AppFoldableTransition>
  </div>
</template>

<style scoped lang="scss">
@use '../../styles/fonts' as *;

.app-expandable-button {
  .expandable-button {
    width: 100%;
    cursor: pointer;

    .expandable-button-wrapper {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: var(--spacing-8);

      .left-icon {
        padding-right: var(--spacing-4);
        &.disabled {
          color: var(--color-neutral-50);
        }
      }
      .content {
        flex: 1;
        min-width: 0;
        overflow: hidden;

        .summary-content {
          .title {
            &.disabled {
              color: var(--color-neutral-50);
            }
            @include font-label-m-bold;
            color: var(--color-soft-black);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .subtitle {
            @include font-label-s-light;
            color: var(--color-neutral-70);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            &.disabled {
              color: var(--color-neutral-50);
            }
          }
        }
      }

      .right-icon {
        &.disabled {
          color: var(--color-neutral-50);
        }
      }
    }
  }
}
</style>
